import React, { memo, useRef, useState, useLayoutEffect } from 'react';
import CroppedRow from '@lib/components/CroppedRow/CroppedRow';
import classNames from 'classnames';
import styles from './TabBar.module.scss';
import TabBarMoreItems from './TabBarMoreItems';
import {
  ActiveItem,
  TabBarItem as TabBarItemType,
  OFFSET_WIDTH,
  AFTER_TABS_WIDTH,
} from './constants';
import TabBarItem from './TabBarItem';

export type TabBarVariant = 'default' | 'fullWidth';

type Props = {
  active?: ActiveItem;
  className?: string;
  items: TabBarItemType[];
  onActiveChange: (item: TabBarItemType) => void;
  withoutCrop?: boolean;
  useSidePaddings?: boolean;
  useScrollIntoView?: boolean;
  renderAfterTabs?: React.ReactNode;
  /** Variant style: 'default' or 'fullWidth' (centered items, taller height, no border) */
  variant?: TabBarVariant;
};

function TabBar({
  active,
  className,
  useSidePaddings,
  items,
  onActiveChange,
  withoutCrop,
  useScrollIntoView,
  renderAfterTabs,
  variant = 'default',
}: Props) {
  const isFullWidth = variant === 'fullWidth';
  // Reserve space for "More" button + afterTabs when present
  const afterTabsOffset = renderAfterTabs ? AFTER_TABS_WIDTH : 0;

  // Measure available width from outerWrapper (has proper width constraint)
  const outerWrapperRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const element = outerWrapperRef.current;
    if (!element) return undefined;

    // Initial measurement
    const measureWidth = () => {
      const { width } = element.getBoundingClientRect();
      setMaxWidth((prev) => (prev !== width ? width : prev));
    };
    measureWidth();

    // Watch for resize changes
    const resizeObserver = new ResizeObserver(() => {
      measureWidth();
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []); // Only set up observer once

  return (
    <div
      ref={outerWrapperRef}
      className={classNames(
        styles.outerWrapper,
        {
          [styles.outerWrapperWithSidePaddings]: useSidePaddings,
          [styles.fullWidth]: isFullWidth,
        },
        'TabBarComponent',
        className,
      )}
    >
      <div className={styles.wrapper}>
        <CroppedRow
          className={styles.root}
          items={items}
          offsetWidth={OFFSET_WIDTH + afterTabsOffset}
          maxWidth={maxWidth}
          renderItem={({ key, ...restItemProps }) => (
            <TabBarItem
              key={key}
              active={active}
              onActiveChange={onActiveChange}
              useScrollIntoView={useScrollIntoView}
              isFullWidth={isFullWidth}
              {...restItemProps}
            />
          )}
          renderMoreItems={(renderMoreProps) => (
            <TabBarMoreItems
              active={active}
              onActiveChange={onActiveChange}
              {...renderMoreProps}
            />
          )}
          withoutCrop={withoutCrop}
          renderAfter={
            renderAfterTabs ? (
              <div className={styles.afterTabs}>{renderAfterTabs}</div>
            ) : undefined
          }
        />
      </div>
    </div>
  );
}

export default memo(TabBar);
